import HHIntegration from '@/orm/models/Integration/HHIntegration';
import { computed, ref, nextTick } from '@vue/composition-api';
import useValidation from '@/compositions/useValidation';
import { response } from '@/orm/functions';

const hhVacanciesRef = ref([]);
const integrationDataRef = ref(null);

export default function useHHIntegration() {
  const { withApiErrorHandling, loading, errorMessage } = useValidation();

  const integrationData = computed(() => {
    return integrationDataRef.value;
  });

  const isAuthorized = computed(() => {
    const data = integrationData.value;

    if (!data) {
      return false;
    }

    const hasEmployerId = data?.employerId != null
      && data?.employerId !== '';

    const hasCode = data?.hasCode === true
      || data?.hasCode === 'true';

    const hasToken = data?.hasToken === true
      || data?.hasToken === 'true';

    return hasEmployerId && hasCode && hasToken;
  });

  async function fetchVacancies() {
    return withApiErrorHandling(async () => {
      const vacancies = await response(HHIntegration.api().fetchVacancies(), []);
      hhVacanciesRef.value = Array.isArray(vacancies) ? vacancies : [];
      return hhVacanciesRef.value;
    });
  }

  async function fetchIntegrationData() {
    return withApiErrorHandling(async () => {
      const data = await response(HHIntegration.api().getIntegrationData(), null);
      integrationDataRef.value = data && typeof data === 'object' ? data : null;
    });
  }

  async function fetchSettings() {
    return withApiErrorHandling(async () => {
      return response(HHIntegration.api().getSettings());
    });
  }

  async function updateSettings(payload) {
    return withApiErrorHandling(async () => {
      return response(HHIntegration.api().updateSettings(payload));
    });
  }

  async function importVacancy(hhVacancyId, payload = {}) {
    return withApiErrorHandling(async () => {
      return response(HHIntegration.api().importVacancy(hhVacancyId, payload));
    });
  }

  async function importVacancies(hhVacancyIds, payload = {}) {
    return withApiErrorHandling(async () => {
      if (!Array.isArray(hhVacancyIds) || hhVacancyIds.length === 0) {
        return;
      }

      return response(Promise.all(
        hhVacancyIds.map((hhVacancyId) => HHIntegration.api().importVacancy(hhVacancyId, payload)),
      ));
    });
  }

  async function importAllVacancies(payload = {}) {
    return withApiErrorHandling(async () => {
      return response(HHIntegration.api().importAllVacancies(payload));
    });
  }

  async function getCodeRedirectUri() {
    try {
      const apiResponse = await HHIntegration.api().getCodeRedirectUri();
      const data = await response(apiResponse, null);
      return data?.redirectUri || data?.url || data || null;
    } catch (error) {
      return null;
    }
  }

  async function handleAuthorization(router) {
    const url = await getCodeRedirectUri();
    if (url) {
      window.location.href = url;
    } else if (router) {
      router.push({ name: 'hh-authorization-error' });
    }
  }

  async function goToHHImportPage(router) {
    try {
      await fetchIntegrationData();
      await nextTick();

      if (isAuthorized.value) {
        router.push({ name: 'hh-vacancies-import' });
      } else {
        await handleAuthorization(router);
      }
    } catch (error) {
      router.push({ name: 'hh-authorization-error' });
    }
  }

  async function removeIntegration() {
    return withApiErrorHandling(async () => {
      await response(HHIntegration.api().removeIntegration(), null);
      integrationDataRef.value = null;
    });
  }

  async function attachToExisting(vacancyId, hhVacancyId) {
    return withApiErrorHandling(async () => {
      return response(HHIntegration.api().attachToExisting(vacancyId, hhVacancyId), null);
    });
  }

  return {
    hhVacancies: computed(() => hhVacanciesRef.value),
    integrationData,
    isAuthorized,
    loading: computed(() => loading.value),
    errorMessage: computed(() => errorMessage.value),
    fetchVacancies,
    fetchIntegrationData,
    fetchSettings,
    updateSettings,
    importVacancy,
    importVacancies,
    importAllVacancies,
    getCodeRedirectUri,
    handleAuthorization,
    goToHHImportPage,
    removeIntegration,
    attachToExisting,
  };
}
